import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { sortBy } from 'lodash';
import { Statistic } from '@monorepo/react-components';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import { Trade } from '@monorepo/types';
import { Table } from '../../components/table/table';
import { useStore } from '../../helpers/use-store';

export interface Props {
  accountId: string;
  positionId: string;
  positionState: 'open' | 'closed';
}

const TradesTable: FunctionComponent<Props> = ({
  accountId,
  positionId,
  positionState,
}) => {
  const {
    dataStore: { positionStore },
  } = useStore();
  const [trades, setTrades] = useState<Trade[]>([]);
  const { loadingState, updateLoadingState } = useLoading();
  const { t } = useTranslation(['common', 'position']);

  useEffect(() => {
    const init = async () => {
      try {
        updateLoadingState(LoadingState.Loading);
        const result = await positionStore.getTrades(accountId, positionId);

        setTrades(result.results);
      } catch (e) {
        console.error(e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, [accountId]);

  const sortedTrades = sortBy(trades, 'date');

  const columns: ColumnsType<Trade> = [
    {
      title: t('position:trades_table.trade_id'),
      dataIndex: 'traderId',
      width: '5%',
      render: (_, trade) => {
        return <span>{trade.id}</span>;
      },
    },
    {
      title: t('position:trades_table.time'),
      width: '10%',
      dataIndex: 'date',
      render: (_, trade) => {
        return <span>{dayjs(trade.date).format('MMM D hh:mm:ss')}</span>;
      },
    },
    {
      title: t('position:positions.side'),
      width: '5%',

      dataIndex: 'side',
    },
    {
      title: t('position:trades_table.action'),
      width: '10%',
      dataIndex: 'action',
      render: (_, trade) => {
        const isPositionClosed = positionState === 'closed';
        const isFirst = sortedTrades[0].id === trade.id;

        if (isFirst) {
          return t('position:trades_table.open');
        }

        const isLast = sortedTrades[sortedTrades.length - 1].id === trade.id;

        if (isLast && isPositionClosed) {
          return t('common:close');
        }

        if (trade.isCover) {
          return t('position:trades_table.decrease');
        }

        return t('position:trades_table.increase');
      },
    },
    {
      title: t('position:trades_table.price'),
      dataIndex: 'price',
      width: '10%',

      render: (_, trade) => {
        return <Statistic value={trade.price} precision={2} prefix={'$'} />;
      },
    },
    {
      title: t('common:fee'),
      dataIndex: 'fee',
      width: '10%',

      render: (_, trade) => {
        return <Statistic value={trade.fee} precision={2} prefix={'$'} />;
      },
    },
    {
      title: t('position:positions.qty'),
      width: '3%',

      dataIndex: 'quantity',
    },
  ];

  const isLoading = loadingState === LoadingState.Loading;

  return (
    <Table
      size={'small'}
      bordered={false}
      loading={isLoading}
      columns={columns}
      dataSource={trades}
      rowKey="id"
    />
  );
};

export default TradesTable;
